import React, { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import { Add, Delete, DeleteForever } from '@mui/icons-material';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { Button, Divider } from '@mui/material';
import { dateString, priceToString } from 'shared/utils';
import { VARIANTS } from 'shared/constants';
import {
  createUpgrade, deleteUpgrade, searchProducts, searchUpgrades,
} from 'shared/api/upgrades';
import { Title, TooltipIconButton } from 'shared/components/abstract/interactables';
import { StripePriceEditor } from 'shared/components/data-display/editors';
import StickyHeadTable from 'shared/components/table/StickyHeadTable';
import PageControls from 'shared/components/table/PageControls';
import ExportButton from 'shared/components/table/ExportButton';
import useDataTable from 'shared/components/table/useDataTable';
import { viewPath as tenantSearchPath } from 'shared/routes/tenant';
import { useRequest } from 'shared/hooks';
import { StaticSearchableSelect } from 'shared/components/select/SearchableSelects';
import { getUpgradesPath, viewPath as buildingSearchPath } from 'shared/routes/building';

/**
 * @param {Function} forceRefresh - Function to force refresh the upgrade list
 * @param {boolean} disabled - Whether the creator is disabled
 */
function UpgradeCreator({ forceRefresh, disabled }) {
  const { request } = useRequest();
  const { search } = useLocation();
  const history = useHistory();

  const [product, setProduct] = useState(undefined);
  const searchParams = new URLSearchParams(search);
  const productId = searchParams.get('product_id') || '';
  const price = parseInt(searchParams.get('price'), 10) || 0;
  const [products, setProducts] = useState([]);

  useEffect(() => {
    request(searchProducts(''))
      .then((res) => {
        if (res) setProducts(res.rows);
      });
  }, [request]);

  const handleCreate = async (e) => {
    e.preventDefault();

    const result = await request(createUpgrade(productId, price), 'Created upgrade plan');
    if (!result) return;
    forceRefresh();
  };

  const handleProductChange = (value) => {
    setProduct(value);
    history.push(getUpgradesPath(value?.id, price));
  };

  const handlePriceChange = (value) => {
    history.push(getUpgradesPath(productId, value));
  };

  return (
    <form onSubmit={handleCreate}>
      <Grid
        container
        rowSpacing={1.5}
        columnSpacing={1}
        sx={{
          alignItems: 'center',
          maxWidth: 800,
        }}
      >
        <Grid size={{ xs: 12, lg: 4 }}>
          <StaticSearchableSelect
            getOptionLabel={(o) => o?.qos?.description}
            onChange={handleProductChange}
            staticOptions={products}
            label="Service"
            selectedOption={product}
            placeholder="All"
          />
        </Grid>
        <Grid size={{ xs: 12, lg: 4 }}>
          <StripePriceEditor
            label="Monthly Price"
            value={price}
            setValue={handlePriceChange}
          />
        </Grid>
        <Grid size={{ xs: 12, lg: 4 }}>
          <Button
            disabled={!productId || !price || disabled}
            startIcon={<Add />}
            variant={VARIANTS.contained}
            type="submit"
          >
            Create Upgrade
          </Button>
        </Grid>
      </Grid>
    </form>
  );
}

function buildingCountLink(row) {
  return (
    row?.building_count > 0
      ? (
        <Link to={`${buildingSearchPath}?plan_id=${row.id}`}>
          {row.building_count}
        </Link>
      ) : 0);
}

function subscriptionCountLink(row) {
  return (row?.sub_count > 0
    ? (
      <Link to={`${tenantSearchPath}?plan_id=${row.id}`}>
        {row.sub_count}
      </Link>
    ) : 0);
}

function deleteIcon(row, onDelete) {
  const disabled = row?.building_count > 0 || row?.sub_count > 0;

  return (
    <TooltipIconButton
      icon={disabled ? DeleteForever : Delete}
      className={`trash ${disabled > 0 ? 'disable' : ''}`}
      disabled={disabled}
      onClick={(() => onDelete(row.id))}
      message="Delete upgrade"
      disabledTip="Buildings and subscriptions must be 0 before deleting"
      placement="right"
    />
  );
}

export default function UpgradeSearch() {
  const [updateCount, setUpdateCount] = useState(0);
  const { request } = useRequest();
  const forceRefresh = () => setUpdateCount(updateCount + 1);
  const {
    exportCSV,
    isLoading,
    onReorder,
    pageBack,
    pageFirst,
    pageForward,
    pageLast,
    tableState,
  } = useDataTable({ searchRequest: searchUpgrades, updateCount });

  const {
    availablePages,
    filterState,
    rows,
  } = tableState;

  const {
    order,
    orderBy,
    page,
  } = filterState;

  const onDelete = async (plan) => {
    const result = await request(deleteUpgrade(plan), 'Deleted upgrade plan');
    if (result) {
      forceRefresh();
    }
  };

  const columns = [
    {
      name: 'Speed',
      formatter: (row) => row.product.qos.description,
    },
    {
      name: 'Price',
      formatter: (row) => priceToString(row.price),
      orderBy: 'price',
    },
    {
      name: 'Buildings',
      formatter: (row) => buildingCountLink(row),
      orderBy: 'building_count',
    },
    {
      name: 'Subscriptions',
      formatter: (row) => subscriptionCountLink(row),
      orderBy: 'sub_count',
    },
    {
      name: 'Created At',
      formatter: (row) => dateString(row.created_at),
      orderBy: 'plans.created_at',
    },
    {
      name: 'Delete',
      formatter: (row) => deleteIcon(row, onDelete),
    },
  ];

  return (
    <Grid
      container
      spacing={1}
      sx={{
        justifyContent: 'end',
      }}
    >
      <Title title="Manage Upgrades" />
      <Grid size={12}>
        <UpgradeCreator
          forceRefresh={forceRefresh}
          disabled={isLoading || !rows || rows.length > 0}
        />
      </Grid>
      <Grid size={12}>
        <Divider />
      </Grid>
      <Grid size={12}>
        <StickyHeadTable
          columns={columns}
          currentOrder={order}
          currentOrderBy={orderBy}
          noRowsText=""
          onReorder={onReorder}
          rowId="id"
          rows={rows}
          striped
          className="table-height-large"
        />
      </Grid>
      <Grid size={{ md: 3, xs: 12 }}>
        <PageControls
          hidden={availablePages < 2}
          availablePages={availablePages}
          disabled={isLoading}
          page={page}
          pageBack={pageBack}
          pageFirst={pageFirst}
          pageForward={pageForward}
          pageLast={pageLast}
        />
      </Grid>
      <Grid>
        <ExportButton
          onExport={exportCSV}
          disabled={isLoading}
          hidden={!rows || rows.length === 0}
        />
      </Grid>
    </Grid>
  );
}
